import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import styles from './button.scss?inline';
import { BUTTON_TYPES } from '../constants';
import { createRef, ref } from 'lit/directives/ref.js';

export default class SotbitButton extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        disabled: { type: Boolean, reflect: true },
        download: { type: Boolean },
        href: { type: String },
        type: { type: String },
        value: { type: String, reflect: true },
        name: { type: String },
        outline: { type: Boolean },
        background: { type: Boolean },
        linkTarget: { type: String, attribute: 'link-target' },
        isLink: { type: Boolean, attribute: 'is-link' },
        inline: { type: Boolean },
        hasRightIcon: { type: Boolean, state: true },
        hasLeftIcon: { type: Boolean, state: true },
        form: { type: String },
        ariaLabel: { type: String, attribute: 'aria-label' },
        dataTooltip: { type: String, attribute: 'data-tooltip' },
    };

    constructor() {
        super();

        this._buttonRef = createRef();
        this._slotIconLeftRef = createRef();
        this._slotIconRightRef = createRef();
        this._slotContentRef = createRef();
        this.hasRightIcon = false;
        this.hasLeftIcon = false;
        this.download = false;
        this._hiddenBtn = null;
    }

    get value() {
        return this._value;
    }

    set value(value) {
        this._value = value;
    }

    get formNode() {
        const formId = this.getAttribute('form');
        return formId ? (this._form ??= document.querySelector(`form[id="${formId}"]`)) : this.closest('form');
    }

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this._handleButtonClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._handleButtonClick);
        this._getHiddenButton()?.remove();
        this._form = null;
    }

    updated(_changedProperties) {
        super.updated(_changedProperties);

        if (_changedProperties.has('form') && _changedProperties.get('form') !== this.form) {
            this._form = null;
        }
    }

    _handleButtonClick = (e) => {
        if (this.disabled) {
            e.preventDefault();
            return;
        }

        if (e.defaultPrevented) {
            return;
        }

        if (this._buttonRef.value && !e.composedPath().includes(this._buttonRef.value)) {
            this._buttonRef?.value?.click();
        }

        if (this.type === BUTTON_TYPES.SUBMIT && this.formNode) {
            if (!this.formNode.contains(this._getHiddenButton())) {
                this.formNode.appendChild(this._getHiddenButton());
            }

            this.formNode.requestSubmit(this._getHiddenButton());
        }
    };

    _getHiddenButton() {
        if (!this._hiddenBtn) {
            this._hiddenBtn = document.createElement('button');
        }

        this._hiddenBtn.setAttribute('type', 'submit');
        this._hiddenBtn.setAttribute('name', this.name);
        this._hiddenBtn.setAttribute('value', this.value);
        this._hiddenBtn.textContent = this.value;
        this._hiddenBtn.style.setProperty('display', 'none');

        return this._hiddenBtn;
    }

    _handleSlotChange() {
        const slotIconRightElements = this._slotIconRightRef?.value?.assignedElements();
        const slotIconLeftElements = this._slotIconLeftRef?.value?.assignedElements();

        this.hasRightIcon = slotIconRightElements?.length > 0 || false;
        this.hasLeftIcon = slotIconLeftElements?.length > 0 || false;
    }

    templateInnerContent() {
        return html` <slot ${ref(this._slotIconLeftRef)} name="icon-left"></slot>
            <slot ${ref(this._slotContentRef)} name="content"
                ><span class="content" part="content">${this.value}</span></slot
            >
            <slot ${ref(this._slotIconRightRef)} name="icon-right"></slot>`;
    }

    render() {
        const aria = this.ariaLabel ? this.ariaLabel : (this.dataTooltip ?? '');

        return html`
            ${this.outline ? html`<div class="outline" part="outline"></div>` : ``}
            ${this.background ? html`<div class="background" part="background"></div>` : ``}
            ${this.isLink
                ? html`
                      <a
                          ?download="${this.download}"
                          ?disabled="${this.disabled}"
                          part="button"
                          class="btn ${this.hasRightIcon ? 'has-right-icon' : ''} ${this.hasLeftIcon
                              ? 'has-left-icon'
                              : ''}"
                          target="${this.linkTarget || nothing}"
                          ${ref(this._buttonRef)}
                          @slotchange="${this._handleSlotChange}"
                          href="${this.href || nothing}"
                      >
                          ${this.templateInnerContent()}
                      </a>
                  `
                : html`
                      <button
                          ?disabled="${this.disabled}"
                          part="button"
                          type="${this.type || nothing}"
                          form="${this.form || nothing}"
                          name="${this.name || nothing}"
                          value="${this.value || nothing}"
                          class="btn ${this.hasRightIcon ? 'has-right-icon' : ''} ${this.hasLeftIcon
                              ? 'has-left-icon'
                              : ''}"
                          @slotchange="${this._handleSlotChange}"
                          ${ref(this._buttonRef)}
                          aria-label="${aria}"
                      >
                          ${this.templateInnerContent()}
                      </button>
                  `}
        `;
    }
}
